import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import LoadingOverlay from 'react-loading-overlay'
import Table from 'react-table'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import PeopleIcon from '@material-ui/icons/People'
import SegmentsService from '../../services/segments.service'
import ModalButtonWrap from '../modal/modalButton'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchSegments,
  createSegment,
  updateSegment,
  deleteSegment,
  setSelectedSegment,
} from '../../store/actions/segments'
import constants from '../../constants'
import {
  QuotsButton,
  ActionButton,
  BaseList,
  BaseForm,
  BaseFormActions,
  BaseFormErrors,
  AddButton,
  SaveButton,
  CancelButton,
  ConfirmButton,
} from '../styled'
import SegmentForm from './SegmentForm'
import { editableFields } from '../../utils/companion'

LoadingOverlay.propTypes = undefined

const { grey, darkGrey } = constants.colors

const TrStyles = () => ({
  style: { borderBottom: `2px solid ${darkGrey}`, backgroundColor: grey },
})
const TheadThProps = () => ({ style: { border: 'none' } })
const TdProps = () => ({
  style: { borderLeft: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
})
const TrGroupProps = () => ({ style: { borderBottom: 'none' } })
const TbodyProps = () => ({ style: { maxHeight: '16rem' } })

const emptySegment = () => ({ name: '', search_criteria: { filters: [] } })

const PAGE_SIZE = 5

function SegmentsContent({ open }) {
  const dispatch = useDispatch()
  const segments = useSelector(state => state.segments.segments)
  const loading = useSelector(state => state.segments.loading)
  const error = useSelector(state => state.segments.error)
  const selectedSegment = useSelector(state => state.segments.selectedSegment)
  const storeFields = useSelector(state => state.users.fields)

  const formFields = editableFields(storeFields).map(f => ({
    name: f.name,
    label: f.label,
    values: Array.isArray(f.values) ? f.values.map(v => v.Value).filter(Boolean) : [],
  }))

  const [view, setView] = useState('list') // 'list' | 'add' | 'edit' | 'delete' | 'preview'
  const [formSegment, setFormSegment] = useState(emptySegment())
  const [previewUsers, setPreviewUsers] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setView('list')
      dispatch(fetchSegments(constants.session.getId()))
    }
  }, [open])

  const openAdd = () => {
    setFormSegment(emptySegment())
    setView('add')
  }

  const openEdit = segment => {
    setFormSegment({ ...segment, search_criteria: { filters: [...segment.search_criteria.filters] } })
    setView('edit')
  }

  const onAdd = () => {
    if (!formSegment.name.trim()) return
    dispatch(createSegment(formSegment))
    setView('list')
  }

  const onEdit = () => {
    if (!formSegment.name.trim()) return
    dispatch(updateSegment(formSegment.segment_id, formSegment))
    setView('list')
  }

  const openPreview = segment => {
    setPreviewUsers([])
    setPreviewLoading(true)
    setView('preview')
    SegmentsService.segmentPreview(constants.session.getId(), segment.segment_id)
      .then(users => setPreviewUsers(users))
      .catch(() => setPreviewUsers([]))
      .finally(() => setPreviewLoading(false))
  }

  const onDeleteRequest = segment => {
    dispatch(setSelectedSegment(segment))
    setView('delete')
  }

  const onDeleteConfirm = () => {
    if (selectedSegment) dispatch(deleteSegment(selectedSegment.segment_id))
    setView('list')
  }

  const columns = [
    {
      Header: '#',
      id: 'index',
      Cell: ({ index }) => <span>{index + 1}</span>,
      maxWidth: 50,
      sortable: false,
      resizable: false,
    },
    {
      Header: 'Nom',
      accessor: 'name',
      sortable: true,
    },
    {
      Header: 'Participants',
      accessor: 'users_count',
      sortable: true,
      maxWidth: 110,
    },
    {
      Header: 'Créé le',
      accessor: 'created_at',
      sortable: true,
      Cell: ({ value }) => (value ? new Date(value).toLocaleDateString('fr-FR') : ''),
    },
    {
      Header: '',
      id: 'actions',
      sortable: false,
      resizable: false,
      maxWidth: 100,
      Cell: ({ original }) => (
        <>
          <Tooltip title="Éditer le segment">
            <ActionButton onClick={() => openEdit(original)}>
              <EditIcon />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Aperçu des participants">
            <ActionButton onClick={() => openPreview(original)}>
              <PeopleIcon />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Supprimer le segment">
            <ActionButton onClick={() => onDeleteRequest(original)}>
              <DeleteIcon />
            </ActionButton>
          </Tooltip>
        </>
      ),
    },
  ]

  return (
    <LoadingOverlay
      active={loading}
      spinner
      text="Chargement..."
      styles={{ wrapper: { height: '100%' } }}
    >
      {view === 'list' && (
        <BaseList className="segment-modal">
          <Typography variant="h6" gutterBottom>
            Gérer les segments
          </Typography>
          <Table
            columns={columns}
            data={segments}
            showPagination={true}
            showPaginationBottom={false}
            showPaginationTop={true}
            defaultPageSize={PAGE_SIZE}
            defaultSorted={[{ id: 'created_at', desc: true }]}
            style={{ width: 'calc(100% - 1px)', fontFamily: 'roboto' }}
            className="waiting-lists"
            getTheadProps={TrStyles}
            getTheadThProps={TheadThProps}
            getTdProps={TdProps}
            getTrGroupProps={TrGroupProps}
            getTbodyProps={TbodyProps}
            previousText="Précédent"
            nextText="Suivant"
            ofText="sur"
            rowsText="lignes"
            noDataText=""
          />
          <BaseFormErrors error={error}>{error}</BaseFormErrors>
          <BaseFormActions>
            <AddButton onClick={openAdd}>AJOUTER UN SEGMENT</AddButton>
          </BaseFormActions>
        </BaseList>
      )}

      {view === 'preview' && (
        <BaseList className="segment-preview-modal">
          <Typography variant="h6" gutterBottom>
            Participants du segment
          </Typography>
          <LoadingOverlay
            active={previewLoading}
            spinner
            text="Chargement..."
            styles={{ wrapper: { height: '100%', width: '100%' } }}
          >
            <Table
              columns={[
                { Header: 'Email', accessor: 'email', sortable: true },
                { Header: 'Prénom', accessor: 'first_name', sortable: true },
                { Header: 'Nom', accessor: 'last_name', sortable: true },
              ]}
              data={previewUsers}
              showPagination={true}
              showPaginationTop={true}
              showPaginationBottom={false}
              defaultPageSize={PAGE_SIZE}
              style={{ width: 'calc(100% - 1px)', fontFamily: 'roboto' }}
              className="waiting-lists"
              getTheadProps={TrStyles}
              getTheadThProps={TheadThProps}
              getTdProps={TdProps}
              getTrGroupProps={TrGroupProps}
              getTbodyProps={TbodyProps}
              previousText="Précédent"
              nextText="Suivant"
              ofText="sur"
              rowsText="lignes"
              noDataText=""
            />
          </LoadingOverlay>
          <BaseFormActions>
            <CancelButton onClick={() => setView('list')}>RETOUR</CancelButton>
          </BaseFormActions>
        </BaseList>
      )}

      {view === 'delete' && (
        <BaseForm>
          <Typography variant="h6" gutterBottom>
            Êtes-vous sûr de vouloir supprimer ce segment ?
          </Typography>
          <BaseFormActions>
            <CancelButton onClick={() => setView('list')}>ANNULER</CancelButton>
            <ConfirmButton onClick={onDeleteConfirm}>VALIDER</ConfirmButton>
          </BaseFormActions>
        </BaseForm>
      )}

      {(view === 'add' || view === 'edit') && (
        <BaseForm className="segment-edit-modal">
          <Typography variant="h6" gutterBottom>
            {view === 'add' ? 'Ajouter un segment :' : 'Éditer le segment :'}
          </Typography>
          <br />
          <SegmentForm segment={formSegment} onChange={setFormSegment} fields={formFields} />
          <BaseFormActions>
            <CancelButton onClick={() => setView('list')}>ANNULER</CancelButton>
            {view === 'add'
              ? <AddButton onClick={onAdd} disabled={!formSegment.name.trim() || formSegment.search_criteria.filters.length === 0}>AJOUTER</AddButton>
              : <SaveButton onClick={onEdit} disabled={!formSegment.name.trim() || formSegment.search_criteria.filters.length === 0}>ENREGISTRER</SaveButton>
            }
          </BaseFormActions>
        </BaseForm>
      )}
    </LoadingOverlay>
  )
}

export default function Segments() {
  const [open, setOpen] = useState(false)

  return (
    <ModalButtonWrap
      open={open}
      right={true}
      onBlur={() => setOpen(false)}
      content={<SegmentsContent open={open} />}
    >
      <QuotsButton onClick={() => setOpen(true)}>SEGMENTS</QuotsButton>
    </ModalButtonWrap>
  )
}
